import { Box, Grid, HStack, Stack, Text } from "@chakra-ui/layout";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { UtilizationReportDataSource } from "@src/__generated__/graphql";
import { StatsCard } from "@src/components/modules/reports/user-profit-report/components/stats/card";
import { UserProfitPerMonthStatCard } from "@src/components/modules/reports/user-profit-report/components/stats/user-profit-per-month";
import { UserProfitReportTable } from "@src/components/modules/reports/user-profit-report/components/tables";
import { UserProfitReportGroupedByTable } from "@src/components/modules/reports/user-profit-report/components/tables/grouped-by";
import { UserProfitReportStore } from "@src/components/modules/reports/user-profit-report/store";
import { GroupByMenu } from "@src/components/modules/reports/utilization/components/group-by-menu";
import { useUtilizationFiltersFromQuery } from "@src/components/modules/reports/utilization/hooks/filtersFromQuery";
import { useUtilizationOptions } from "@src/components/modules/reports/utilization/hooks/options";
import { BillableUtilizationStatCard } from "@src/components/modules/reports/utilization/stat-cards/BillableUtilization";
import { UtilizationUserReportModal } from "@src/components/modules/reports/utilization/UtilizationUserReportModal";
import {
  DataTable,
  DropdownButton,
  ExportButton,
} from "@src/components/ui-kit";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";

export const UserProfitReport = observer(function UserProfitReport() {
  const appStore = useStore();
  const store = useLocalObservable(() => new UserProfitReportStore(appStore));

  useUtilizationFiltersFromQuery(store);
  useUtilizationOptions(store);

  return (
    <DataTable>
      <DataTable.NavBar>
        <Box py="4">
          <FiltersPanelRoot filters={store.where}>
            <Stack>
              <HStack justifyContent="space-between">
                <HStack spacing="2">
                  <PeriodPicker
                    value={store.dateRange}
                    onChange={store.handleChangeDateRange}
                  />
                  <GroupByMenu
                    activeGroupByOption={store.activeGroupByOption}
                    groupByOptions={store.groupByOptions}
                    onGroupByChange={store.handleChangeGroupBy}
                    onResetPage={store.pagination.resetPage}
                  />

                  <FiltersPanelFilters
                    innerMenuPlacement="right-end"
                    clearButtonSide="right"
                  />
                </HStack>

                <HStack spacing="2">
                  <Menu>
                    <MenuButton as={DropdownButton}>
                      <Trans>Data source</Trans>
                      <span>:</span>
                      <Text as="span" ml="1" color="blue.500">
                        {store.activeDataSourceOption?.label}
                      </Text>
                    </MenuButton>
                    <MenuList>
                      {store.dataSourceOptions.map(({ value, label }) => (
                        <MenuItem
                          key={value}
                          onClick={() => {
                            store.handleChangeDataSource(value);
                          }}
                        >
                          {label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  {can("utilizationReportProfit_query_all") && (
                    <ExportButton
                      onClick={store.exportReport}
                      isLoading={store.isExportLoading}
                    />
                  )}
                </HStack>
              </HStack>
            </Stack>
          </FiltersPanelRoot>
        </Box>
      </DataTable.NavBar>

      <Grid p="4" gap="3" templateColumns="repeat(4, auto)">
        <BillableUtilizationStatCard
          percent={store.tableStatsData?.utilization_percentage_billable ?? 0}
          totalTime={store.tableStatsData?.available_time ?? 0}
          combinedTime={store.tableStatsData?.combined_time ?? 0}
          allocatedTime={store.tableStatsData?.allocated_time ?? 0}
          budgetedTime={store.tableStatsData?.budgeted_time ?? 0}
          trackedTime={store.tableStatsData?.tracked_time ?? 0}
          timeOffTime={store.tableStatsData?.time_off ?? 0}
          combinedTimeBillable={
            store.tableStatsData?.combined_time_billable ?? 0
          }
          allocatedTimeBillable={
            store.tableStatsData?.allocated_time_billable ?? 0
          }
          budgetedTimeBillable={
            store.tableStatsData?.budgeted_time_billable ?? 0
          }
          trackedTimeBillable={store.tableStatsData?.tracked_time_billable ?? 0}
          activeDataSource={store.activeDataSourceOption?.value}
          utilizationRange={{
            from:
              appStore.workspaceStore?.settings
                ?.default_utilization_range_from ?? 80,
            to:
              appStore.workspaceStore?.settings?.default_utilization_range_to ??
              100,
          }}
        />

        <StatsCard
          title={t`Revenue estimate`}
          value={
            store.dataSource === UtilizationReportDataSource.TaskPositions
              ? store.tableStatsData?.budgeted_amount_billable
              : store.tableStatsData?.combined_amount_billable
          }
        />

        <StatsCard
          title={t`Internal cost`}
          value={store.tableStatsData?.internal_cost}
        />

        <UserProfitPerMonthStatCard
          profit={
            store.dataSource === UtilizationReportDataSource.Combined
              ? store.tableStatsData?.combined_profit_amount
              : store.tableStatsData?.budgeted_profit_amount
          }
          revenue={
            store.dataSource === UtilizationReportDataSource.Combined
              ? store.tableStatsData?.combined_amount_billable
              : store.tableStatsData?.budgeted_amount_billable
          }
        />
      </Grid>

      {store.groupBy ? (
        <UserProfitReportGroupedByTable store={store} />
      ) : (
        <UserProfitReportTable store={store} />
      )}

      <Pagination store={store.pagination} />

      <UtilizationUserReportModal
        store={store.userModalStore}
        activeDataSource={store.activeDataSourceOption?.value}
      />
    </DataTable>
  );
});
